import type {FC, ReactElement} from 'react';
import {useRef} from 'react';
import React, {useState} from 'react';
import {useIsInViewport} from '@Hooks/useIsInViewport';
import {isIOS, isSafari} from 'react-device-detect';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import styles from './grid-media-item.module.scss';
import type {GridItemBaseStorage, GridItemProps} from '../grid-item';
import {GridItem} from '../grid-item';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import {useAppSelector} from '@/hooks';

export enum GridMediaItemTagType {
  COST,
  SOURCE,
}

export interface CostTag {
  type: GridMediaItemTagType.COST;
  containerStyle: string;
  price: string;
  icon: string;
  textStyle: string;
}

export interface SourceTag {
  type: GridMediaItemTagType.SOURCE;
  containerStyle: string;
  iconStyle: string;
}

export type GridMediaItemTag = CostTag | SourceTag;

interface GridMediaItemProps extends GridItemProps {
  thumb?: ReactElement<any>;
  title?: string;
  className?: string;
  paddedThumb?: boolean;
  showViewportMediaOnly?: boolean;
  isImageLoaded?: boolean;
  isLoading?: boolean;
  tooltipContent?: ReactElement<any>;
}

export interface GridMediaItemStorage extends GridItemBaseStorage {
  width?: number;
  height?: number;
  isLoading?: boolean;
}

/**
 * @class GridMediaItem
 */
export const GridMediaItem: FC<GridMediaItemProps> = ({paddedThumb = true, thumb = null, title = '', isImageLoaded = false, isLoading = false, ...props}) => {
  const [gridRef, setGridRef] = useState<HTMLDivElement | null>(null);
  const isInViewPort = useIsInViewport(gridRef as HTMLElement);
  const [isHoveringOnTooltip, setIsHoveringOnTooltip] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const isGridScrolling = useAppSelector((state) => {
    return state.grids.gridsHashmap[props.gridId ?? '']?.isScrolling ?? false;
  });

  const getLoader = (): ReactElement<any> | null => {
    if (isLoading) {
      return <CircularProgressLoader color={LOADER_COLOR.WHITE} size={LOADER_SIZE.DEFAULT} classes={`${styles.loadingItem} ${styles.loaderPosition}`} />;
    }

    return null;
  };

  const getLoaderOverlay = (): ReactElement<any> | null => {
    if (isLoading) {
      return <div className={`_full-width _full-height flex-center ${styles.loadingOverlay}`} />;
    }

    return null;
  };

  const handleMouseEnter = (): void => {
    clearTimeout(timer.current);
    setIsHoveringOnTooltip(true);
  };

  const handleMouseLeave = (): void => {
    timer.current = window.setTimeout(() => {
      setIsHoveringOnTooltip(false);
    }, 80);
  };

  const prepareContent = (data: ReactElement<any>): ReactElement<any> => {
    if (props.tooltipContent && !isIOS) {
      return (
        <OverlayTrigger
          trigger={['hover', 'focus']}
          placement="top"
          show={isHoveringOnTooltip && !isGridScrolling}
          overlay={
            <Tooltip onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={`spacing-m-b-1  _rounded-arrow ${styles.tooltip}`}>
              {props.tooltipContent}
            </Tooltip>
          }
          delay={{show: 0, hide: 0}}
        >
          {data}
        </OverlayTrigger>
      );
    }

    return data;
  };

  return (
    <GridItem {...props}>
      {prepareContent(
        <div
          className={`flex-v-row flex-center _full-width _full-height radius-12 ${isImageLoaded ? '' : styles.thumbBackground} ${
            props.hasTransparency ? styles.thumbWithTransparency : ''
          } ${isLoading ? styles.loadingContainer : ''} thumb`}
          ref={setGridRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {thumb && (isInViewPort || !(isIOS || isSafari))
            ? React.cloneElement(thumb, {
                className: `${thumb.props.className} ${styles.thumb} ${paddedThumb ? styles.paddedThumb : ''} ${isLoading ? styles.loadingItem : ''} `,
              })
            : null}
          {getLoaderOverlay()}
          {getLoader()}
        </div>
      )}
    </GridItem>
  );
};
